import { Timeline } from '@mui/lab';
import {
  timelineItemClasses,
} from '@mui/lab/TimelineItem';
import dayjs from 'dayjs';
import _ from 'lodash';
import { RecentEventsDayGrouping } from './RecentEventsDayGrouping';
import { RecentEvent } from './types';
import React from 'react';

export const RecentEventsTimeline = (props: { recentEvents: RecentEvent[]; fetchNextPage: () => void; hasNextPage: boolean; isFetchingNextPage?: boolean; }) => {
  const eventsByDate = _.groupBy(props.recentEvents, p => dayjs(p.eventDate).startOf('day').toString());
  const observerRef = React.useRef<IntersectionObserver | null>(null);
  const sentinelRef = React.useRef<HTMLDivElement | null>(null);
  const fetchingRef = React.useRef<boolean>(!!props.isFetchingNextPage);
  const hasNextRef = React.useRef<boolean>(!!props.hasNextPage);

  React.useEffect(() => { fetchingRef.current = !!props.isFetchingNextPage; }, [props.isFetchingNextPage]);
  React.useEffect(() => { hasNextRef.current = !!props.hasNextPage; }, [props.hasNextPage]);

  React.useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;

    if (observerRef.current == null) {
      observerRef.current = new IntersectionObserver(entries => {
        const entry = entries[0];
        if (!entry.isIntersecting) return;
        if (!hasNextRef.current || fetchingRef.current) return;
        props.fetchNextPage();
      }, { root: null, rootMargin: '200px', threshold: 0.1 });
    }

    observerRef.current.observe(el);
    return () => observerRef.current?.disconnect();
  }, [props.fetchNextPage]);

  // Auto-preload: if the sentinel is already visible (e.g., page has plenty of space),
  // fetch until we either run out of pages or fetching starts.
  React.useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;

    const isVisible = () => {
      const rect = el.getBoundingClientRect();
      const vh = window.innerHeight || document.documentElement.clientHeight;
      return rect.top <= vh && rect.bottom >= 0;
    };

    if (isVisible() && hasNextRef.current && !fetchingRef.current) {
      props.fetchNextPage();
    }
  }, [props.recentEvents.length, props.hasNextPage, props.isFetchingNextPage, props.fetchNextPage]);

  return (
    <Timeline
      position='right'
      sx={{
        [`& .${timelineItemClasses.root}:before`]: {
          flex: 0,
          padding: 0,
        },
        marginBlockStart: 0,
      }}
    >
      {Object.entries(eventsByDate).map(([date, events]) => (
        <RecentEventsDayGrouping key={date} recentEvents={events} />
      ))}
      <div ref={sentinelRef} />
    </Timeline>);
};